// bernie
import { debounce, SerializedData, Store } from 'bernie-core';
import { Logger, NOOP_LOGGER } from 'bernie-logger';

// mobx
import { action, makeObservable, observable, computed, toJS } from 'mobx';
import { computedFn } from 'mobx-utils';

// models
import { User, UserInfo, UserSignIn, Program } from 'models';

import { AuthState, UserAuthSource } from '../sources';
import AffiliateHubLogger from '../util/affiliate-hub-logger';

import isEqual from 'lodash.isequal';
import ProgramStatus from 'stores/ProgramStatus';
import { UserPzAccountDetails } from 'src/models/user';

const STORAGE_LISTENING_INTERVAL = 25; //in ms

interface InternalAuthState {
  accessToken: string;
  idToken: string;
}

export class UserAuthStore extends Store {
  public currentUser: UserInfo;

  public authenticated: AuthState = AuthState.UNKNOWN;

  public authorized: AuthState;

  public userDetails;

  public userProgramRelationships;

  private internalAuthState: InternalAuthState;

  private readonly userAuthSource: UserAuthSource;

  public constructor(userAuthSource: UserAuthSource, state: SerializedData = {}, logger: Logger = NOOP_LOGGER) {
    super(state, logger);

    makeObservable(this, {
      authenticated: observable,
      userDetails: observable,
      userProgramRelationships: observable,
      currentUser: observable,
      partnerizeAccount: computed,
      checkAuth: action,
      createAuthSessionOrLogoutTo: action,
      logout: action,
      fetchUserDetails: action,
    });

    this.authorized = AuthState.UNKNOWN;
    this.userAuthSource = userAuthSource;
    this.internalAuthState = null;

    // listening to local storage changes, if something changed, refresh auth state
    if (typeof window !== 'undefined') {
      this.storageListener = debounce(this.storageListener.bind(this), STORAGE_LISTENING_INTERVAL);
      window.addEventListener('storage', this.storageListener);
    }
  }

  private getLogger(): AffiliateHubLogger {
    return this.logger as AffiliateHubLogger;
  }

  public get currentUserDetails(): User {
    if (this.userDetails) {
      return toJS(this.userDetails).body;
    }

    return null;
  }

  @computed
  public get partnerizeAccount(): UserPzAccountDetails {
    if (this.userProgramRelationships && this.userProgramRelationships.body.length) {
      return (
        this.userProgramRelationships &&
        this.userProgramRelationships.body
          .filter((userProg) => {
            return userProg.partnerIdentity.affiliateNetwork === 'PARTNERIZE';
          })
          .map((programRelationship) => {
            return {
              accountName: programRelationship.accountName,
              partnerId: programRelationship.partnerIdentity.partnerId,
              affiliateNetwork: programRelationship.partnerIdentity.affiliateNetwork,
            };
          })[0]
      );
    }

    return null;
  }

  private storageListener() {
    this.didInternalAuthStateChange().then((changed) => {
      if (changed) {
        this.checkAuth();
      }
    });
  }

  public isInLoginRedirect() {
    return this.userAuthSource.isInLoginRedirect();
  }

  // This is called from the Auth Callback page that Okta redirects to.
  // Its job is to turn that action into a logged-in session and does that
  // by asking the lower auth layers to figure this out.
  @action
  public createAuthSessionOrLogoutTo(unAuthedPath: string) {
    this.userAuthSource
      .createAuthenticatedSession()
      .then(() => {
        // A previous dev said:
        // Need to trigger check auth and can't rely on storage listener as
        // documentation suggests that the storage listener is not triggered for documents
        // that make the storage change.
        this.checkAuth();
      })
      .catch((err) => {
        this.getLogger().warn('CREATE_AUTHENTICATED_SESSION_FAILED', JSON.stringify(err));
        // Ideally want to send the error to the path, so that it can display the error message
        // but we don't that at the moment
        this.logout(unAuthedPath);
      });
  }

  public async authRedirect(params) {
    return this.userAuthSource.authRedirect(params);
  }

  public initiateLogin(redirection: boolean, fromUri: string = null) {
    if (this.userAuthSource) {
      this.userAuthSource.initiateLogin(redirection, fromUri);
    }
  }

  public hydrate(data: SerializedData): void {
    if (data.userAuthSource) {
      Object.assign(this.userAuthSource, data.userAuthSource);
      this.userAuthSource.hydrate();
    }

    delete data.userAuthSource;
    Object.assign(this, data);
  }

  public hydrateHistory = (history) => {
    this.userAuthSource.hydrateHistory(history);
  };

  private async fetchUserInfo(): Promise<UserInfo> {
    return this.userAuthSource.fetchUserInfo();
  }

  @action
  public async fetchUserProgramRelationships() {
    this.userProgramRelationships = await this.userAuthSource.getProgramRelationships(this.currentUser);

    return this.userProgramRelationships;
  }

  @action
  public async fetchUserDetails() {
    this.userDetails = await this.userAuthSource.getUserDetails(this.currentUser);

    return this.userDetails;
  }

  private async didInternalAuthStateChange() {
    const currentInternalAuthState = await this.getCurrentInternalAuthState();

    return !isEqual(currentInternalAuthState, this.internalAuthState);
  }

  private async getCurrentInternalAuthState() {
    const accessToken = await this.userAuthSource.getAccessToken();
    const idToken = await this.userAuthSource.getIdToken();

    return { accessToken, idToken };
  }

  // Intentionally named checkAuth as it checks both authentication & authorization state
  @action
  public async checkAuth() {
    const authenticated: AuthState = await this.userAuthSource.isAuthenticated();

    if (authenticated === AuthState.TRUE) {
      try {
        this.currentUser = await this.fetchUserInfo();
        this.authenticated = authenticated;
      } catch (error) {
        this.getLogger().warn('FETCH_USER_INFO_FAILED', JSON.stringify(error));
        this.logout('/home');
      }
    } else if (authenticated === AuthState.FALSE) {
      this.authorized = AuthState.FALSE;
      this.authenticated = AuthState.FALSE;
      this.currentUser = null;
      this.userDetails = null;
      this.userProgramRelationships = null;
    } else {
      this.authorized = AuthState.UNKNOWN;
      this.authenticated = AuthState.UNKNOWN;
      this.currentUser = null;
      this.userDetails = null;
      this.userProgramRelationships = null;
    }

    this.internalAuthState = await this.getCurrentInternalAuthState();
  }

  public updateUserProgramRelationship(
    brand: string,
    posa: string,
    network: string,
    partnerId: string,
    isExisting: boolean,
    programReference?: string,
    accountName?: string,
  ) {
    return fetch(`/api/v1/users/${this.currentUser.email}/programs`, {
      method: isExisting ? 'PUT' : 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token: this.internalAuthState.accessToken,
        email: this.currentUser.email,
        brand,
        posa,
        network,
        partnerId,
        programReference,
        accountName,
      }),
    }).then((response) =>
      response.json().then((data) => {
        data['status'] = response.status;

        // this will refresh the user program relationships
        this.fetchUserProgramRelationships();

        return data;
      }),
    );
  }

  public signIn({ username, password, deviceToken }: UserSignIn) {
    return this.userAuthSource.signIn({ username, password, deviceToken });
  }

  public signInWithToken(token: string) {
    return this.userAuthSource.signInWithToken(token);
  }

  @action
  public async logout(logoutPath: string) {
    try {
      await this.userAuthSource.revokeAndLogout(logoutPath);
    } catch (ex) {
      this.getLogger().warn('FAILED_TO_LOGOUT', ex);
    }
    this.currentUser = null;
    this.authenticated = AuthState.FALSE;
    this.authorized = AuthState.FALSE;
    this.userProgramRelationships = null;
    this.internalAuthState = null;
  }

  public getActiveUserProgram = computedFn((program: Program) => {
    const programRelationship = this.getRelationshipForProgram(program);
    if (programRelationship !== null) {
      const partnershipStatus = programRelationship.affiliatePartnership.partnershipStatus;

      return {
        programStatus: partnershipStatus,
        active: partnershipStatus === ProgramStatus.APPROVED,
        preview:
          partnershipStatus === 'PENDING' ||
          partnershipStatus === 'UNKNOWN' ||
          partnershipStatus === null ||
          partnershipStatus === undefined,
        programReference: programRelationship.programReference,
      };
    }

    return {
      programStatus: ProgramStatus.UNKNOWN,
      active: false,
      preview: true,
    };
  });

  public getRelationshipForProgram = computedFn((program: Program) => {
    let programRelationship = null;
    const userInfo = this.currentUser;
    const userId = userInfo && userInfo.email ? userInfo.email : null;

    if (userId) {
      const userProgram = this.userProgramRelationships
        ? this.userProgramRelationships.body.filter((userProg) => {
            return (
              userProg.id.userId === userId && userProg.id.brand === program.brand && userProg.id.posa === program.posa
            );
          })
        : [];

      if (userProgram.length) {
        programRelationship = userProgram[0];
      }
    }

    return programRelationship;
  });

  public async closeSession() {
    return this.userAuthSource.closeSession();
  }
}
